/** Detaliu comandă Ultra (App-Ultra.dc.html) — timeline, mesaje cu editorul, review + revizii. */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useUltraJob, useUltraMessages, useMutation, useInvalidate, qk } from '../api/hooks';
import { acceptUltra, postUltraMessage, requestUltraRevision } from '../api/endpoints';
import { humanError } from '../api/client';
import { useToast } from '../context/ToastContext';
import { UltraStatusBadge, ULTRA_TIMELINE } from '../components/ultra';
import { ErrorBlock, Modal, Skeleton, Spinner } from '../components/ui';
import { formatDateTime, timeAgo } from '../lib/format';

export function UltraJobPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const job = useUltraJob(jobId);
  const messages = useUltraMessages(jobId);
  const invalidate = useInvalidate();
  const toast = useToast();
  const [msg, setMsg] = useState('');
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');

  useEffect(() => {
    document.title = 'Comandă Ultra — Reclame Video';
  }, []);

  const sendMsg = useMutation({
    mutationFn: () => postUltraMessage(jobId!, { body: msg.trim() }),
    onSuccess: () => {
      invalidate([qk.ultraMessages(jobId!)]);
      setMsg('');
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });
  const acceptMut = useMutation({
    mutationFn: () => acceptUltra(jobId!),
    onSuccess: () => {
      invalidate([qk.ultraJob(jobId!), qk.ultra]);
      toast.push({ kind: 'ok', title: 'Ai acceptat livrarea!' });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Eroare', body: humanError(err) }),
  });
  const revisionMut = useMutation({
    mutationFn: () => requestUltraRevision(jobId!, revisionReason.trim()),
    onSuccess: () => {
      invalidate([qk.ultraJob(jobId!)]);
      setRevisionOpen(false);
      setRevisionReason('');
      toast.push({ kind: 'ok', title: 'Cerere de revizie trimisă' });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Revizie indisponibilă', body: humanError(err) }),
  });

  if (job.isLoading) return <Skeleton height={220} />;
  if (job.isError || !job.data) return <ErrorBlock message="Nu am găsit această comandă Ultra." />;

  const j = job.data;
  const activeIdx = ULTRA_TIMELINE.findIndex((t) => t.status.includes(j.status));

  return (
    <div>
      <Link to="/ultra" style={{ fontSize: 13, fontWeight: 800, color: 'var(--muted)', textDecoration: 'none' }}>← Comenzi Ultra</Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
        <h1 className="bricolage" style={{ fontWeight: 800, fontSize: 26 }}>Clip Ultra</h1>
        <UltraStatusBadge status={j.status} />
      </div>

      {/* Timeline */}
      <div style={{ display: 'flex', gap: 8, margin: '20px 0 24px', flexWrap: 'wrap' }}>
        {ULTRA_TIMELINE.map((t, i) => (
          <div key={t.label} style={{ flex: 1, minWidth: 120 }}>
            <div style={{ height: 5, borderRadius: 3, background: i <= activeIdx ? 'var(--accent)' : 'var(--bg2)' }} />
            <span style={{ fontSize: 11.5, fontWeight: 800, color: i === activeIdx ? 'var(--ink)' : 'var(--muted)', marginTop: 4, display: 'block' }}>{t.label}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 24, alignItems: 'start' }} className="dash-grid">
        {/* Mesaje */}
        <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2 className="bricolage" style={{ fontSize: 17, fontWeight: 800 }}>Mesaje cu editorul</h2>
          <div data-testid="ultra-messages" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflow: 'auto' }}>
            {(messages.data?.items ?? []).length === 0 && <p style={{ fontSize: 13, color: 'var(--muted)' }}>Niciun mesaj încă. Scrie-i editorului dacă ai preferințe.</p>}
            {(messages.data?.items ?? []).map((m) => (
              <div key={m.id} style={{ display: 'flex', justifyContent: m.sender === 'client' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '82%', padding: '10px 12px', borderRadius: 12, fontSize: 13.5, background: m.sender === 'client' ? 'var(--accent)' : 'var(--bg2)', color: m.sender === 'client' ? 'var(--accent-ink)' : 'var(--ink)' }}>
                  {m.body}
                  <div style={{ fontSize: 10.5, opacity: 0.7, marginTop: 3 }}>{timeAgo(m.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="input" data-testid="ultra-message-input" placeholder="Scrie editorului…" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && msg.trim() && sendMsg.mutate()} />
            <button className="btn btn-primary btn-sm" data-testid="ultra-message-send" onClick={() => sendMsg.mutate()} disabled={sendMsg.isPending || !msg.trim()}>
              {sendMsg.isPending ? <Spinner /> : 'Trimite'}
            </button>
          </div>
        </div>

        {/* Panou lateral: brief + review */}
        <aside style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card" style={{ padding: 16 }}>
            <h3 className="bricolage" style={{ fontSize: 15, fontWeight: 800, marginBottom: 8 }}>Brief-ul tău</h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)', whiteSpace: 'pre-wrap' }}>{j.brief || '—'}</p>
          </div>
          <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--muted)' }}>Livrare estimată</span>
              <span style={{ fontWeight: 800 }}>{j.slaDeadline ? formatDateTime(j.slaDeadline) : '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--muted)' }}>Revizii incluse</span>
              <span className="tnum" style={{ fontWeight: 800 }}>{j.revisionsLeft}/{j.revisionsIncluded}</span>
            </div>
            {j.editor && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--muted)' }}>Editor</span>
                <span style={{ fontWeight: 800 }}>{j.editor.firstName}</span>
              </div>
            )}
          </div>
          {j.status === 'livrat' && (
            <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, borderColor: 'var(--warn)' }}>
              <p style={{ margin: 0, fontWeight: 800 }}>Review final</p>
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--muted)' }}>Editorul a livrat. Acceptă sau cere o revizie.</p>
              <button className="btn btn-primary btn-sm" data-testid="ultra-accept" onClick={() => acceptMut.mutate()} disabled={acceptMut.isPending}>
                {acceptMut.isPending ? <Spinner /> : 'Acceptă livrarea'}
              </button>
              <button className="btn btn-ghost btn-sm" data-testid="ultra-revision" onClick={() => setRevisionOpen(true)} disabled={j.revisionsLeft <= 0}>
                {j.revisionsLeft > 0 ? `Cere revizie (${j.revisionsLeft} rămase)` : 'Fără revizii rămase'}
              </button>
            </div>
          )}
        </aside>
      </div>

      <Modal open={revisionOpen} onClose={() => setRevisionOpen(false)} title="Cere o revizie">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label className="label">Ce ai vrea schimbat?<textarea className="input" rows={4} value={revisionReason} onChange={(e) => setRevisionReason(e.target.value)} /></label>
          <button className="btn btn-primary" onClick={() => revisionMut.mutate()} disabled={revisionMut.isPending || revisionReason.trim().length < 3}>
            {revisionMut.isPending ? <Spinner /> : 'Trimite cererea'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
